import { DateTime } from './DateTime'
import { Incident } from './Incident'
import { SnapshotComparison } from './SnapshotComparison'
import { ActionExecution } from './ActionExecution'
import { TraceSummary } from './TraceSummary'

export type ErrorId = string

export enum IssueType {
    ERROR = 'error',
    NON_FATALS = 'non-fatals',
    SLOWSQL = 'slowsql',
    RELEASE = 'release',
    INCIDENT = 'incident',
    OBSERVATION = 'observation',
    NOTICE = 'notice',
    WARNING = 'warning',
    DEPRECATED = 'deprecated',
}

export enum ErrorGroupState {
    NEW = 0,
    OPEN = 1,
    RESOLVED = 2,
    IGNORED = 3,
    ALWAYS_IGNORED = 4,
}

export interface ErrorContext {
    errorGroup: ErrorGroup
    snapshotComparison?: SnapshotComparison
    operations: OperationsItem[]
    totalOccurances: number
    transactionIds: any[]
    occurances: {
        occurances: Occurence[]
    }
    error: Error
    offset: number
}

export type ErrorGroup = DefaultErrorGroup | ErrorGroupObservation | ErrorGroupIncident

export interface ErrorItemList {
    errors: ErrorGroup[]
    totalErrors: number
    totalPages: number
    currentPage: number
}

export interface DefaultErrorGroup {
    id: string
    appId: string
    source: string
    level: IssueType
    type: string
    exceptionType: string
    lastMessage: string
    lastStackTrace: StacktraceFrame[]
    occurances: number
    occurancesSinceLastRelease: number
    status: ErrorStatusCode
    isOnDefaultAlwaysIgnoreList: boolean
    firstOccured: ErrorGroupOccurrence
    lastOccured: ErrorGroupOccurrence
    lastOpened: ErrorGroupOccurrence
    transactionIds: string[]
    annotations: ErrorAnnotations
    environments: string[]
    lastNotifyOccurances: number
    lastNotifyDate: null
    fingerprint: string
    services: string[]
    notify: boolean
    originatingFunction: string
    relatedErrors?: ErrorItemList | null
    relatedAlertActionExecutions?: ActionExecution[]
    checkId?: number
}

export enum ObservationState {
    IGNORE = -2,
    UNKNOWN = -1,
    OK = 0,
    WARNING = 1,
    ERROR = 2,
}

export interface ErrorGroupObservationAffectedOrigin {
    timestamp: number
    state: ObservationState
    value: string
}

export interface ErrorGroupObservation extends DefaultErrorGroup {
    level: IssueType.OBSERVATION
    observation: {
        state: ObservationState
        affectedOrigins: {
            [host: string]: ErrorGroupObservationAffectedOrigin
        }
        label: string
        labelOff: string
        valueLabel: string
        description: string
        link: string
        type: string
        traces?: { profiles: TraceSummary[] }
    }
}

export interface ErrorGroupIncident extends DefaultErrorGroup {
    level: IssueType.INCIDENT
    incident: Incident
}

export interface StacktraceFrame {
    function: string
    file: string
    line: number
    code?: string
    previousException?: { type: string; message: string; code: number }
}
type ErrorGroupOccurrence = DateTime

export interface ErrorAnnotations {
    [key: string]: string
}
interface OperationsItem {
    name: string
    count: number
}
export interface Occurence {
    start: string
    end: string
    count: number
}
export interface Error {
    source: string
    file: string
    line: number
    code?: string
    type: string
    message: string
    annotations: ErrorAnnotations
    time: string
    stackTrace: StacktraceFrame[]
    appId: string
    sId: string
    opId: string
    errorId: string
    fileLine: string
    group: string
    occurances: number
    level: IssueType
}

export type ErrorStatusCode = 1 | 2 | 3 | 4

export const errorGroupTitle = (error: ErrorGroup): string => {
    switch (error.level) {
        case IssueType.DEPRECATED:
            return '[Deprecation] ' + error.lastMessage
        case IssueType.NOTICE:
            return '[Notice] ' + error.lastMessage
        case IssueType.WARNING:
            return '[Warning] ' + error.lastMessage
        case IssueType.SLOWSQL: {
            const query = error.type
            if (query.indexOf('SELECT') !== -1) {
                return query.replace('SELECT', 'SELECT ... FROM')
            } else if (query.indexOf('DELETE') !== -1) {
                return query.replace('DELETE', 'DELETE FROM')
            } else if (query.indexOf('INSERT') !== -1) {
                return query.replace('INSERT', 'INSERT INTO')
            } else {
                return error.type
            }
        }
        default:
            return error.type
    }
}

export const errorLevelIsNonFatal = (level: IssueType): boolean =>
    level === IssueType.NOTICE || level === IssueType.WARNING || level === IssueType.DEPRECATED

export const errorGroupIsNonCritical = (error: ErrorGroup): boolean => error.level !== IssueType.ERROR

export const nameForErrorLevelMultiple = (level: IssueType): string => {
    switch (level) {
        case IssueType.ERROR:
            return 'Errors'
        case IssueType.NON_FATALS:
            return 'Non-fatals'
        case IssueType.SLOWSQL:
            return 'Slow SQL'
        case IssueType.RELEASE:
            return 'Releases'
        case IssueType.INCIDENT:
            return 'Incidents'
        case IssueType.OBSERVATION:
            return 'Observations'
        case IssueType.NOTICE:
            return 'Notices'
        case IssueType.WARNING:
            return 'Warnings'
        case IssueType.DEPRECATED:
            return 'Deprecations'
        default:
            return 'Unknown'
    }
}
